import {
  CLOSE_CODES,
  REALTIME_PATH,
  REALTIME_PROTOCOL_VERSION,
  serverMessageSchema,
  type MatchView,
} from '@soe/protocol';

export type ConnectionStatus =
  | 'connecting'
  | 'open'
  | 'reconnecting'
  | 'offline'
  /** The server ended the session (sign-out, sign-in elsewhere, deletion). */
  | 'session-ended'
  /** This tab runs an old client; it must reload. */
  | 'outdated';

export interface ConnectionEvents {
  onStatus: (status: ConnectionStatus) => void;
  onMatch: (match: MatchView | null, serverNow: number) => void;
}

const PING_MS = 25_000;
const MAX_BACKOFF_MS = 15_000;

/**
 * Read-only realtime channel. Reconnects with exponential backoff; stops for good when
 * the session has ended or the protocol is outdated. Only server-validated messages
 * reach the app.
 */
export class RealtimeConnection {
  private socket: WebSocket | null = null;
  private attempt = 0;
  private retryTimer: number | undefined;
  private pingTimer: number | undefined;
  private stopped = false;
  private readonly controller = new AbortController();

  constructor(private readonly events: ConnectionEvents) {
    window.addEventListener(
      'online',
      () => {
        this.reconnectNow();
      },
      { signal: this.controller.signal },
    );
    window.addEventListener(
      'offline',
      () => {
        this.events.onStatus('offline');
      },
      { signal: this.controller.signal },
    );
  }

  start(): void {
    this.open();
  }

  /** Permanently closes the connection and releases every timer and listener. */
  stop(): void {
    this.stopped = true;
    this.controller.abort();
    window.clearTimeout(this.retryTimer);
    window.clearInterval(this.pingTimer);
    this.socket?.close(1000, 'client closed');
    this.socket = null;
  }

  private open(): void {
    if (this.stopped) return;
    if (!navigator.onLine) {
      this.events.onStatus('offline');
      return;
    }
    this.events.onStatus(this.attempt === 0 ? 'connecting' : 'reconnecting');
    const url = new URL(`${REALTIME_PATH}?v=${REALTIME_PROTOCOL_VERSION}`, window.location.href);
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    const socket = new WebSocket(url);
    this.socket = socket;

    socket.addEventListener('open', () => {
      this.attempt = 0;
      this.events.onStatus('open');
      window.clearInterval(this.pingTimer);
      this.pingTimer = window.setInterval(() => {
        if (socket.readyState === WebSocket.OPEN)
          socket.send(JSON.stringify({ v: 1, type: 'ping' }));
      }, PING_MS);
    });

    socket.addEventListener('message', (event: MessageEvent<unknown>) => {
      if (typeof event.data !== 'string') return;
      let raw: unknown;
      try {
        raw = JSON.parse(event.data);
      } catch {
        return;
      }
      const parsed = serverMessageSchema.safeParse(raw);
      if (!parsed.success) return;
      const message = parsed.data;
      if (message.type === 'match.state') this.events.onMatch(message.match, Date.now());
    });

    socket.addEventListener('close', (event) => {
      window.clearInterval(this.pingTimer);
      if (this.socket === socket) this.socket = null;
      if (this.stopped) return;
      if (event.code === CLOSE_CODES.SESSION_ENDED) {
        this.stopped = true;
        this.events.onStatus('session-ended');
        return;
      }
      if (event.code === CLOSE_CODES.PROTOCOL_MISMATCH) {
        this.stopped = true;
        this.events.onStatus('outdated');
        return;
      }
      this.scheduleRetry();
    });
  }

  private scheduleRetry(): void {
    this.attempt += 1;
    const delay = Math.min(MAX_BACKOFF_MS, 500 * 2 ** this.attempt) * (0.75 + Math.random() * 0.5);
    this.events.onStatus(navigator.onLine ? 'reconnecting' : 'offline');
    window.clearTimeout(this.retryTimer);
    this.retryTimer = window.setTimeout(() => {
      this.open();
    }, delay);
  }

  private reconnectNow(): void {
    if (this.stopped) return;
    if (this.socket) {
      // Back online and the socket survived the outage: say so, instead of staying "offline".
      // A socket still connecting will report through its own open or close event.
      if (this.socket.readyState === WebSocket.OPEN) this.events.onStatus('open');
      return;
    }
    window.clearTimeout(this.retryTimer);
    this.open();
  }
}
